import React, { useState, useEffect, useCallback, useRef } from "react";
import { useHttp } from "../../hooks/useHttp";
import { API_URLS, DEFAULT_HEADERS } from "../../util/constants";
import { useLocation, useNavigate } from "react-router-dom";

/**
 * Termos na área de compras:
 * - Usuário NÃO logado: pode visualizar termos em /termos-e-politicas sem precisar aceitar.
 * - Usuário logado: deve aceitar os termos para continuar; exibe modal full-screen até o aceite.
 *
 * Loading unificado: exibe loader até que auth E (se logado) verificação de termos estejam completos.
 */
const TermsAcceptanceGate = ({ isLoggedIn, userId, isCheckingAuth = false }) => {
  const [needsAccept, setNeedsAccept] = useState(false);
  // Deduplica verificações enquanto o componente está montado.
  // Sem cache em storage: a decisão final vem sempre do backend via `needs_accept`.
  const lastCheckedUserIdRef = useRef(null);
  const inFlightUserIdRef = useRef(null);
  const [content, setContent] = useState(null);
  const [agreeChecked, setAgreeChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { sendRequest } = useHttp();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const didRedirectRef = useRef(false);
  const isOnTermsPage = pathname === "/termos-e-politicas";

  const checkStatus = useCallback(async () => {
    if (!isLoggedIn || !userId) {
      setNeedsAccept(false);
      setContent(null);
      lastCheckedUserIdRef.current = null;
      return;
    }

    // Evita requisições duplicadas enquanto uma checagem anterior estiver em andamento.
    if (inFlightUserIdRef.current === userId) {
      return;
    }

    // Marca imediatamente para bloquear reentrância por múltiplos re-renders.
    inFlightUserIdRef.current = userId;

    try {
      const res = await sendRequest(
        API_URLS.TERMS.STATUS(userId),
        "GET",
        null,
        DEFAULT_HEADERS.JSON,
      );
      const needsAcceptFromBackend = Boolean(res?.needs_accept ?? res?.content?.needs_accept);

      if (res?.success && needsAcceptFromBackend) {
        setNeedsAccept(true);
        setContent({
          terms_content: res?.content?.terms_content ?? res?.terms_content ?? "",
          privacy_content: res?.content?.privacy_content ?? res?.privacy_content ?? "",
        });
      } else {
        setNeedsAccept(false);
        setContent(null);
      }
    } catch {
      setNeedsAccept(false);
      setContent(null);
    } finally {
      inFlightUserIdRef.current = null;
      lastCheckedUserIdRef.current = userId;
    }
  }, [isLoggedIn, userId, sendRequest]);

  useEffect(() => {
    if (isCheckingAuth) return;

    // Usuário não logado ou sem ID: nada para checar
    if (!isLoggedIn || !userId) {
      setNeedsAccept(false);
      setContent(null);
      lastCheckedUserIdRef.current = null;
      return;
    }

    // Já checamos este userId nesta montagem: não refaz a requisição
    if (lastCheckedUserIdRef.current === userId) {
      return;
    }

    checkStatus();
  }, [
    isCheckingAuth,
    isLoggedIn,
    userId,
    checkStatus,
  ]);

  // Redireciona para a página de termos para o usuário conseguir ler.
  // Não bloqueia a renderização do app; o modal fica em cima.
  useEffect(() => {
    if (isCheckingAuth) return;
    if (!needsAccept) return;
    if (!userId) return;

    if (!isOnTermsPage && !didRedirectRef.current) {
      didRedirectRef.current = true;
      navigate("/termos-e-politicas", { replace: true });
    }

    if (isOnTermsPage && needsAccept) {
      didRedirectRef.current = false;
    }
  }, [isCheckingAuth, needsAccept, userId, isOnTermsPage, navigate]);

  const handleAccept = async () => {
    if (!agreeChecked || !userId) return;
    setSubmitting(true);
    try {
      await sendRequest(
        API_URLS.TERMS.ACCEPT,
        "POST",
        JSON.stringify({ action: "accept", user_id: parseInt(userId, 10) }),
        DEFAULT_HEADERS.JSON,
      );
      setNeedsAccept(false);
      setContent(null);
      setAgreeChecked(false);
      // Após aceitar, volta ao fluxo principal (lista de produtos).
      navigate("/produtos", { replace: true });
    } catch (err) {
      console.error("Erro ao aceitar termos:", err);
    } finally {
      setSubmitting(false);
    }
  };


  if (needsAccept && content) {
    return (
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "#FFF",
          zIndex: 99999,
          overflow: "auto",
          padding: "24px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <style>{`
          .terms-acceptance-content,
          .terms-acceptance-content * {
            color: #000 !important;
          }
          .terms-acceptance-content a {
            color: var(--ltn__secondary-color, #071C1F) !important;
            text-decoration: underline;
          }
        `}</style>
        <div className="container" style={{ maxWidth: "800px" }}>
        <img src={"assets/img/logo-full.png"} alt="Logo" style={{  height: "80px", marginBottom: "20px" }} />
          <h1 className="mb-4" style={{ fontSize: "1.5rem", color: "#000" }}>
            Atualização dos Termos de Uso e Política de Privacidade
          </h1>
          <p className="mb-4 text-muted">
            Os termos foram atualizados. Para continuar utilizando o sistema,
            leia e aceite os novos termos.
          </p>

          <div
            className="mb-4 p-3 rounded terms-acceptance-content"
            style={{
              maxHeight: "50vh",
              overflowY: "auto",
              backgroundColor: "#f8f9fa",
              border: "1px solid #e5eaee",
            }}
          >
            <div
              dangerouslySetInnerHTML={{ __html: content.terms_content }}
              className="small"
              style={{ color: "#000" }}
            />
            <div
              dangerouslySetInnerHTML={{ __html: content.privacy_content }}
              className="small"
              style={{ color: "#000" }}
            />
          </div>

          <div className="form-check mb-4">
            <input
              type="checkbox"
              className="form-check-input"
              id="terms-agree"
              checked={agreeChecked}
              onChange={(e) => setAgreeChecked(e.target.checked)}
            />
            <label className="form-check-label" htmlFor="terms-agree">
              Li e aceito os Termos de Uso e a Política de Privacidade
            </label>
          </div>

          <button
            className="btn btn-danger"
            onClick={handleAccept}
            disabled={!agreeChecked || submitting}
            title={submitting ? "Salvando..." : "Aceitar e continuar"}
          >
            {submitting ? "Salvando..." : "Aceitar e continuar"}
          </button>
        </div>
      </div>
    );
  };

  return null;
};

export default TermsAcceptanceGate;
